import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type CaseId, TRANSFORMS } from "../lib/casetransform";

const CASES: Array<{ id: CaseId; label: () => string }> = [
  { id: "upper", label: () => m.cc_case_upper() },
  { id: "lower", label: () => m.cc_case_lower() },
  { id: "title", label: () => m.cc_case_title() },
  { id: "sentence", label: () => m.cc_case_sentence() },
  { id: "camel", label: () => m.cc_case_camel() },
  { id: "pascal", label: () => m.cc_case_pascal() },
  { id: "snake", label: () => m.cc_case_snake() },
  { id: "kebab", label: () => m.cc_case_kebab() },
  { id: "constant", label: () => m.cc_case_constant() },
  { id: "alternating", label: () => m.cc_case_alternating() },
];

const SAMPLE = "Hello world, this is Toolverse";

export function CaseConverter() {
  const uid = useId();
  const [text, setText] = useState(SAMPLE);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const outputs = useMemo(
    () => CASES.map((c) => ({ ...c, value: TRANSFORMS[c.id](text) })),
    [text],
  );

  const copy = async (id: string, value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <Label htmlFor={`${uid}-text`}>{m.cc_input_label()}</Label>
        <textarea
          id={`${uid}-text`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={m.cc_placeholder()}
          className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 text-sm leading-relaxed"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {outputs.map((o) => (
          <div
            key={o.id}
            className="rounded-xl border border-border bg-card/40 p-4"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {o.label()}
              </span>
              <button
                type="button"
                onClick={() => copy(o.id, o.value)}
                aria-label={`${m.cc_copy()} — ${o.label()}`}
                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
              >
                {copiedId === o.id ? (
                  <Check className="h-4 w-4 text-emerald-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
            <p
              data-testid={`cc-${o.id}`}
              className="break-words font-mono text-sm"
            >
              {o.value || "—"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
